/**
 * The **Tasks** entry of the 72 px icon rail, between *Workspace* and *Favorites*
 * (`docs/13-tasks-section-proposal.md`, user decision 2026-09-15).
 *
 * It is a **link**, not a pane slot — exactly like *My work*: clicking it navigates to `/tasks`
 * rather than swapping the sidebar's content, so it needs no new `RailSlot` and the pane keeps
 * whatever it was showing. Its geometry is the rail's own (`.ez-rail__item` / `.ez-rail__tile`,
 * `apps/web/src/styles.css`): the same 40 × 40 tile, the same 12 px label, the same hover and
 * selected states. Nothing about the rail is re-measured here, because the Tasks entry is ours
 * and the rail's geometry is monday's.
 *
 * The badge is `tasks.counts`: the number of tasks on **My Tasks**, tinted as an alert when any
 * of them is overdue. It is live — `task.changed` invalidates it, so completing a task in
 * another tab takes the number down without a reload.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon, icons } from '@elizmonday/ui';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import { useTRPC } from '../../lib/trpc';
import { useTasksRealtime } from './useTasksRealtime';
import type { TaskCountsView } from './types';

/** Above this the badge reads `99+`, like monday's own bell. */
export const TASKS_BADGE_MAX = 99;

export function formatTasksBadge(count: number): string {
  return count > TASKS_BADGE_MAX ? `${TASKS_BADGE_MAX}+` : String(count);
}

/** "3 tasks, 1 overdue" — what a screen reader hears instead of a bare number. */
export function tasksBadgeLabel(counts: TaskCountsView | undefined): string {
  if (!counts || counts.mineOpen === 0) return 'Tasks';
  const tasks = `${counts.mineOpen} open ${counts.mineOpen === 1 ? 'task' : 'tasks'}`;
  return counts.overdue > 0 ? `Tasks, ${tasks}, ${counts.overdue} overdue` : `Tasks, ${tasks}`;
}

export function TasksRailEntry() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { revision } = useTasksRealtime(true);

  const options = trpc.tasks.counts.queryOptions();
  const counts = useQuery({ ...options, staleTime: 30_000 });

  /*
   * §6: the event carries no number, so the badge is **re-read**, never patched — the recipient's
   * own rights decide what it may count, and a socket is not an authorisation.
   */
  useEffect(() => {
    if (revision === 0) return;
    void queryClient.invalidateQueries({ queryKey: options.queryKey });
  }, [revision, queryClient, options.queryKey]);

  const data = counts.data as TaskCountsView | undefined;
  /* Open rows only: a task marked done leaves the badge at once, whatever the archive delay keeps in the tab. */
  const open = data?.mineOpen ?? 0;
  const overdue = data?.overdue ?? 0;
  const current = location.pathname === '/tasks' || location.pathname.startsWith('/tasks/');

  return (
    <Link
      className="ez-rail__item"
      to="/tasks"
      aria-current={current ? 'page' : undefined}
      aria-label={tasksBadgeLabel(data)}
      data-testid="rail-tasks"
    >
      <span className="ez-rail__tile">
        <Icon icon={icons.tasks} size={20} ignoreFocusStyle />
        {open > 0 ? (
          <span
            className="ez-rail__badge"
            data-overdue={overdue > 0 ? '' : undefined}
            aria-hidden="true"
            data-testid="rail-tasks-count"
          >
            {formatTasksBadge(open)}
          </span>
        ) : null}
      </span>
      <span className="ez-rail__label">Tasks</span>
    </Link>
  );
}
